import React, {
  forwardRef,
  MutableRefObject,
  useEffect,
  useState,
} from "react";
import classNames from "classnames";

import FormElementErrorMessage from "@/components/botanic/FormElementErrorMessage";
import FormElementLabel from "@/components/botanic/FormElementLabel";
import FormField from "@/components/FormField";

const noop = () => {};

export type InputProps = {
  autoComplete?: string;
  activeOnLoad?: boolean;
  errorMessage?: string;
  errorMessageClasses?: string;
  helperText?: string;
  id: string;
  inputClasses?: string;
  label?: React.ReactNode;
  labelClasses?: string;
  showPlaceholder?: boolean;
  type?: string;
  pattern?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoComplete,
      activeOnLoad = false,
      className = "",
      errorMessage,
      errorMessageClasses,
      helperText,
      id,
      inputClasses = "",
      label,
      labelClasses,
      onBlur = noop,
      onFocus = noop,
      onChange = noop,
      placeholder = "",
      showPlaceholder,
      type = "text",
      value,
      disabled,
      name,
      pattern,
      required,
      ...others
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState(activeOnLoad);

    const newRef: MutableRefObject<HTMLInputElement | null> =
      React.useRef<HTMLInputElement>(null);
    const textInput = (ref || newRef) as MutableRefObject<HTMLInputElement>;

    useEffect(() => {
      if (textInput?.current && !isActive) {
        setIsActive(textInput.current?.value?.length > 0);
      }
    }, [isActive, textInput]);

    return (
      <FormField
        className={classNames(className, { "form__field--active": isActive })}
      >
        {label ? (
          <FormElementLabel
            className={labelClasses}
            hasError={!!errorMessage}
            label={label}
            htmlFor={id}
          />
        ) : (
          <></>
        )}

        <input
          id={id}
          name={name}
          ref={textInput}
          onFocus={(e) => {
            setIsActive(true);
            onFocus(e);
          }}
          placeholder={
            isActive
              ? undefined
              : label && !showPlaceholder
                ? undefined
                : placeholder
          }
          onBlur={(e) => {
            onBlur(e);
            setIsActive(textInput?.current?.value?.length > 0);
          }}
          type={type}
          className={inputClasses}
          value={value}
          onChange={onChange}
          pattern={pattern}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          {...others}
        />
        {helperText && !errorMessage ? <p>{helperText}</p> : <></>}
        {errorMessage ? (
          <FormElementErrorMessage
            className={errorMessageClasses}
            message={errorMessage}
          />
        ) : (
          <></>
        )}
      </FormField>
    );
  },
);

export default Input;
